/**
 * One dossier, opened — everything the form transmits, in the order the patient met it (DE1).
 *
 * Two things are laid out before the raw answers, because they are what a reader decides on:
 *
 * - **the signals, each with the answer that raised it.** `signal.question` is a key of `reponses`,
 *   so the trace already existed in the data and only had to be followed. « Signe neurologique
 *   déclaré » alone is a verdict a reader must take on trust; beside « Une perte de force (jambe) ·
 *   Quelques semaines » it is something a clinician can weigh.
 * - **what the patient declared about themselves**, beside the record they were matched to. This is
 *   the one channel where identity is written by the person concerned rather than inferred, and the
 *   two disagreeing is exactly what somebody needs to see.
 *
 * This component is the dossier's CONTENT and nothing else. The badges live in the page's own bar
 * and the decision left for `CompteRenduPanel` — because the decision is no longer a status somebody
 * picks, it is a document somebody signs, and a second place to settle a dossier is a second answer
 * to « was this one dealt with ».
 */

import { useState } from "react";
import { Link, useLocation } from "react-router";
import type { MessageDescriptor } from "@lingui/core";
import {
  CalendarClock,
  ChevronDown,
  Download,
  ExternalLink,
  History,
  ImageIcon,
  ListChecks,
  Paperclip,
  Stethoscope,
  UserRound,
} from "lucide-react";
import { Plural, SelectOrdinal, Trans, useLingui } from "@lingui/macro";
import { cn } from "@app/lib/utils";
import {
  AXIS_LABELS,
  AXIS_STYLES,
  TAG,
  STRONG_TAG_STYLE,
  STATUT_LABELS,
  answersBySection,
  formatAnswer,
  formatDate,
  formatDateTime,
  imagingLink,
  patientName,
  type DemandeVoisine,
  type DossierDetail as Dossier,
  type ReponseSection,
} from "@app/utils/teleexpertise";

interface DossierDetailProps {
  dossier: Dossier;
  onDownload: (documentId: string) => void;
  /** Where another demand of the same person opens — the same route this dossier was opened by. */
  hrefForDossier: (dossierId: string) => string;
}

export function DossierDetail({ dossier, onDownload, hrefForDossier }: DossierDetailProps) {
  const { i18n, t } = useLingui();
  // Handed on to a neighbouring demand, so « Retour » still knows the list this all started from.
  const location = useLocation();
  const answerWords = { oui: t`Oui`, non: t`Non` };
  const declaredName = [
    dossier.identite_declaree.prenom,
    dossier.identite_declaree.nom,
  ]
    .filter(Boolean)
    .join(" ");
  const matchedName = patientName(dossier.patient);

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto">
      {/* --- header ------------------------------------------------------------------ */}
      <header className="flex flex-col gap-3 border-b border-slate-200 pb-4">
        {/* Their rule, verbatim. « Prioritaire » is unreadable without what produced it, and a
            reader left to supply their own rule is how two surgeons start triaging differently. */}
        {dossier.regle_priorite && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-900">
            <span className="font-semibold">
              <Trans>Règle du formulaire :</Trans>
            </span>{" "}
            {dossier.regle_priorite}
          </p>
        )}

        {/* The counterpart of the refusal at the door: a second dossier is refused while the first
            waits for an avis, so a person appearing twice is always a person whose earlier demand
            was ANSWERED. That answer is the context this screen otherwise does not carry — and
            slate, because what it says is « there is more to read », not a verdict on the patient. */}
        {dossier.historique && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
              <History className="h-3.5 w-3.5" />
              <SelectOrdinal
                value={dossier.historique.rang}
                one="#ʳᵉ demande de ce patient"
                other="#ᵉ demande de ce patient"
              />
            </span>
            {/* Each link is ONE translatable sentence rather than a label glued to a date and a
                status: a sentence assembled from fragments reads as one only in the language it was
                assembled in. */}
            {dossier.historique.precedent && (
              <Link
                to={hrefForDossier(dossier.historique.precedent.dossier_id)}
                state={location.state}
                className="font-medium text-primary hover:underline"
                title={dossier.historique.precedent.suite_donnee_libelle ?? undefined}
              >
                {t`Demande précédente du ${neighbourDate(dossier.historique.precedent, i18n.locale)} — ${neighbourStatus(dossier.historique.precedent, i18n)}`}
              </Link>
            )}
            {dossier.historique.suivant && (
              <Link
                to={hrefForDossier(dossier.historique.suivant.dossier_id)}
                state={location.state}
                className="font-medium text-primary hover:underline"
                title={dossier.historique.suivant.suite_donnee_libelle ?? undefined}
              >
                {t`Demande suivante du ${neighbourDate(dossier.historique.suivant, i18n.locale)} — ${neighbourStatus(dossier.historique.suivant, i18n)}`}
              </Link>
            )}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" />
            <Trans>Reçu le {formatDateTime(dossier.recu_le, i18n.locale) ?? "?"}</Trans>
          </span>
        </div>
      </header>

      {/* --- who this is, as declared -------------------------------------------------- */}
      <section className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
        <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <UserRound className="h-4 w-4 text-primary" />
          <Trans>Ce que le patient a déclaré</Trans>
        </h3>
        <dl className="grid gap-x-6 gap-y-1 text-xs text-slate-600 sm:grid-cols-2">
          <Field label={t`Nom et prénom`} value={declaredName || null} />
          <Field
            label={t`Date de naissance`}
            value={(() => {
              // In French, not the ISO the form validated: this block is read against a patient's
              // card, and « 1994-12-10 » is not the shape anybody checks an identity in.
              const born = formatDate(dossier.identite_declaree.date_naissance, i18n.locale);
              if (!born) return null;
              return dossier.age ? `${born} (${dossier.age} ans)` : born;
            })()}
          />
          <Field label={t`Téléphone`} value={dossier.identite_declaree.telephone} />
          <Field label={t`E-mail`} value={dossier.identite_declaree.email} />
          <Field label={t`Numéro de sécurité sociale`} value={dossier.identite_declaree.nir} />
          {matchedName && !sameName(matchedName, declaredName) && (
            <Field label={t`Dossier rattaché`} value={matchedName} />
          )}
        </dl>
      </section>

      {/* --- signals ----------------------------------------------------------------- */}
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          <Trans>Signaux</Trans>
        </h3>
        {dossier.signaux.length === 0 && (
          <p className="text-sm text-slate-500">
            <Trans>Ce dossier n'a levé aucun signal.</Trans>
          </p>
        )}
        {dossier.signaux.map((signal) => {
          const answer = formatAnswer(signal.reponse, answerWords);
          return (
            <article
              key={signal.question}
              className={cn(
                "rounded-lg border border-slate-200 border-l-4 bg-white p-3",
                // EVERY signal card carries the rail, because a card that has one beside cards that
                // do not reads as the only finished one — which is what happened when only the
                // urgent ones were marked. The rail is the platform's accent by default; red is the
                // exception, and an exception is only legible against a rule.
                signal.axe === "urgence_clinique" && signal.niveau === "fort"
                  ? "border-red-200 border-l-red-500 bg-red-50/30"
                  : "border-l-primary",
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={AXIS_STYLES[signal.axe] ?? AXIS_STYLES.non_classe}>
                  {AXIS_LABELS[signal.axe] ? i18n._(AXIS_LABELS[signal.axe]) : signal.axe}
                </span>
                {signal.tag && (
                  <span
                    className={cn(
                      TAG,
                      STRONG_TAG_STYLE,
                    )}
                  >
                    {signal.tag}
                  </span>
                )}
                <span
                  className={cn(
                    "text-[11px] uppercase tracking-wide",
                    // « Fort » is emphasised but stays neutral in colour: the level is theirs, and
                    // colouring it would say « urgent » about `G2` too — the exact conflation this
                    // screen exists to undo.
                    signal.niveau === "fort"
                      ? "font-semibold text-slate-700"
                      : "text-slate-500",
                  )}
                >
                  {/* Their vocabulary: an unfamiliar level is shown as they sent it. */}
                  {signal.niveau}
                </span>
                <span className="text-[11px] text-slate-400">{signal.question}</span>
              </div>

              <p className="mt-1.5 text-sm font-medium text-slate-900">{signal.libelle}</p>

              {/* The trace back to what the patient actually wrote. Absent rather than blank when
                  the signal names a question the dossier does not hold. */}
              {answer !== null && (
                <div className="mt-2 rounded-md bg-slate-50 px-3 py-2">
                  {signal.question_posee && (
                    <p className="text-[11px] text-slate-500">{signal.question_posee}</p>
                  )}
                  <p className="text-sm text-slate-700">{answer}</p>
                </div>
              )}
            </article>
          );
        })}
      </section>

      {/* --- scores ------------------------------------------------------------------- */}
      {dossier.scores.length > 0 && (
        <section className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
          <h3 className="mb-2 text-sm font-semibold text-slate-900">
            <Trans>Scores fonctionnels</Trans>
          </h3>
          <ul className="space-y-1 text-xs text-slate-600">
            {dossier.scores.map((score) => (
              <li key={score.nom}>
                <span className="font-medium text-slate-800">
                  {score.titre} {score.score_pct} %
                </span>
                {score.classe && <span> — {score.classe}</span>}
                {score.rubriques_renseignees != null && (
                  <span className="text-slate-400">
                    {" "}
                    <Trans>({score.rubriques_renseignees} rubriques)</Trans>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --- referring physician, imaging --------------------------------------------- */}
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
          <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Stethoscope className="h-4 w-4 text-primary" />
            <Trans>Médecin adresseur</Trans>
          </h3>
          {dossier.medecin_traitant ? (
            <dl className="space-y-1 text-xs text-slate-600">
              {/* The form transmits an ADELI, a RPPS, an email and the referral letter — never a
                  name. Said plainly rather than rendered as an empty line, which reads as data that
                  failed to arrive rather than data that is not collected. */}
              <div className="text-slate-400">
                <Trans>Le formulaire ne transmet pas son nom.</Trans>
              </div>
              {dossier.medecin_traitant.email && (
                <div>
                  <a
                    href={`mailto:${dossier.medecin_traitant.email}`}
                    className="text-primary hover:underline"
                  >
                    {dossier.medecin_traitant.email}
                  </a>
                </div>
              )}
              {dossier.medecin_traitant.telephone && (
                <div>{dossier.medecin_traitant.telephone}</div>
              )}
              {dossier.medecin_traitant.rpps && (
                <div>
                  <Trans>RPPS {dossier.medecin_traitant.rpps}</Trans>
                </div>
              )}
              {dossier.medecin_traitant.adeli && (
                <div>
                  <Trans>ADELI {dossier.medecin_traitant.adeli}</Trans>
                </div>
              )}
            </dl>
          ) : (
            <p className="text-xs text-slate-500">
              <Trans>Aucun médecin adresseur déclaré.</Trans>
            </p>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
          <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <ImageIcon className="h-4 w-4 text-primary" />
            <Trans>Examens et imagerie</Trans>
          </h3>
          {dossier.examens.length === 0 ? (
            <p className="text-xs text-slate-500">
              <Trans>Aucun examen déclaré.</Trans>
            </p>
          ) : (
            <ul className="space-y-1.5 text-xs text-slate-600">
              {dossier.examens.map((examen, index) => (
                <li key={`${examen.examen ?? "examen"}-${index}`}>
                  <span className="font-medium text-slate-700">
                    {examen.nature || examen.examen}
                  </span>
                  {examen.date_approximative && (
                    <span className="text-slate-400"> · {examen.date_approximative}</span>
                  )}
                  <ImagingPortal value={examen.lien_images} />
                  {examen.code_acces && (
                    <span className="text-slate-400">
                      {" "}
                      <Trans>(code {examen.code_acces})</Trans>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {dossier.comptes_rendus_manquants.length > 0 && (
            <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
              <Trans>Compte rendu annoncé mais absent :</Trans>{" "}
              {dossier.comptes_rendus_manquants.join(" · ")}
            </p>
          )}
        </div>
      </section>

      {/* --- attachments -------------------------------------------------------------- */}
      {dossier.pieces.length > 0 && (
        <section className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
          <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
            <Paperclip className="h-4 w-4 text-primary" />
            <Trans>Pièces jointes</Trans>
          </h3>
          <ul className="flex flex-wrap gap-2">
            {dossier.pieces.map((piece) => (
              <li key={piece.document_id}>
                <button
                  type="button"
                  onClick={() => onDownload(piece.document_id)}
                  className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 transition-colors hover:border-primary/50 hover:text-primary"
                >
                  <Download className="h-3 w-3" />
                  {piece.nom}
                  {piece.type && <span className="text-slate-400">· {piece.type}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --- every answer, in the order the patient met them --------------------------- */}
      <section className="rounded-xl border border-slate-200 border-l-4 border-l-primary bg-white p-4 shadow-md">
        <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <ListChecks className="h-4 w-4 text-primary" />
          <Trans>Toutes les réponses</Trans>
        </h3>
        {dossier.reponses.length === 0 ? (
          <p className="text-xs text-slate-500">
            <Trans>Aucune réponse transmise.</Trans>
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {answersBySection(dossier.reponses).map((section) => (
              <Section key={section.lettre} section={section} words={answerWords} />
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

/**
 * One step of the form, foldable.
 *
 * Open by default, every one of them: a reader who opens a dossier for the first time must see what
 * it contains, and a screen that hides answers behind seven closed doors is a different failure from
 * the wall it replaced. Folding is there for the second read, when they know which step they want.
 */
function Section({
  section,
  words,
}: {
  section: ReponseSection;
  words: { oui: string; non: string };
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-md border border-slate-100">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left transition-colors hover:bg-slate-50"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 text-slate-400 transition-transform",
            !open && "-rotate-90",
          )}
        />
        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">
          {section.lettre}
        </span>
        <span className="text-[11px] text-slate-500">
          <Plural value={section.reponses.length} one="# réponse" other="# réponses" />
        </span>
      </button>
      {open && (
        <dl className="divide-y divide-slate-100 px-2.5 pb-1.5">
          {section.reponses.map((reponse) => (
            <div key={reponse.cle} className="py-1.5">
              <dt className="text-[11px] text-slate-500">
                <span className="text-slate-400">{reponse.cle}</span> · {reponse.question}
              </dt>
              <dd className="text-sm text-slate-800">
                {formatAnswer(reponse.reponse, words) ?? "—"}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * Whether two writings of a name are the same person's.
 *
 * Case, accents and spacing never distinguish two people — « Aymeric Faure » and « Aymeric FAURE »
 * are one man — and a « dossier rattaché » line that fires on capitalisation is a line the reader
 * learns to skip, which is exactly the line that has to be read when the names really disagree.
 */
function sameName(a: string, b: string): boolean {
  const fold = (value: string) =>
    value
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase();
  return fold(a) === fold(b);
}

/**
 * The two halves of a neighbouring demand's link text, as plain strings.
 *
 * Separate from the sentence that uses them so that the sentence stays ONE message a translator can
 * move words around inside — a date and a status interpolated into it, rather than three fragments
 * concatenated in French word order.
 */
function neighbourDate(demande: DemandeVoisine, locale: string): string {
  return formatDate(demande.recu_le, locale) ?? "?";
}

function neighbourStatus(
  demande: DemandeVoisine,
  i18n: { _: (descriptor: MessageDescriptor) => string },
): string {
  return i18n._(STATUT_LABELS[demande.triage_statut]);
}

function Field({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[11px] text-slate-400">{label}</dt>
      <dd className="text-slate-700">{value}</dd>
    </div>
  );
}

/** An exam's imaging portal: a link when the address opens a site, the patient's words otherwise. */
function ImagingPortal({ value }: { value: string | null | undefined }) {
  const link = imagingLink(value);
  if (!link) {
    const text = value?.trim();
    return text ? <span className="break-words text-slate-500"> · {text}</span> : null;
  }
  const site = link.site;
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noreferrer"
      className="ml-1 inline-flex items-center gap-0.5 text-primary hover:underline"
    >
      <Trans>Voir sur {site}</Trans>
      <ExternalLink className="h-3 w-3" />
    </a>
  );
}
